import { coreApi, createVenueSpecificClient } from '$api'
import {
  PayClassification,
  PayClassificationRequest,
  IPayClassificationTimelineRecordRequest,
  PayClassificationTimelineRecordRequest,
  transformPayClassificationOnGet,
} from '$models'
import {
  hasPermissionsAtVenue,
  singletonQueryClient,
  getCompanyId,
} from '$utils'
// Not from $constants because import cycles can leave it undefined while this file loads
import { PermissionType } from '../../constants/enums'
import sortBy from 'lodash/sortBy'

/** Query keys for pay classification reads, exposed so callers can invalidate the cache after mutations */
export const PAY_CLASSIFICATION_QUERY_KEYS = {
  list: 'get-pay-classifications',
  detail: 'get-pay-classification',
} as const

/** The api will not read or write pay classifications without both of these */
const PAY_CLASSIFICATION_PERMISSIONS = [
  PermissionType.Time,
  PermissionType.Payroll,
]

/**
 * Get all Pay Classifications
 * @param includeDeleted - include the deleted pay classifications
 * @param venueId - optional venue ID to fetch pay classifications for specific venue
 * @returns Promise<PayClassification[]>
 */
export const getAll = async (
  includeDeleted: boolean = false,
  venueId?: string
): Promise<PayClassification[]> => {
  if (!hasPermissionsAtVenue(PAY_CLASSIFICATION_PERMISSIONS, venueId)) return []

  const client = venueId ? createVenueSpecificClient(venueId) : coreApi
  const companyId = venueId || getCompanyId()

  const response = await singletonQueryClient.fetchQuery({
    queryKey: [PAY_CLASSIFICATION_QUERY_KEYS.list, companyId, includeDeleted],
    queryFn: async ({ signal }) => {
      return await client.getPayClassifications(includeDeleted, signal)
    },
  })

  const result = response.map((pc) => transformPayClassificationOnGet(pc))
  return sortBy<PayClassification>(result, 'name')
}

/**
 * Get a Pay Classification by ID
 * @param payClassificationId the ID of the pay classification to get
 * @returns the pay classification with the given ID
 */
export const getPayClassification = async (
  payClassificationId: string
): Promise<PayClassification | null> => {
  if (!hasPermissionsAtVenue(PAY_CLASSIFICATION_PERMISSIONS)) return null

  const companyId = getCompanyId()

  const response = await singletonQueryClient.fetchQuery({
    queryKey: [
      PAY_CLASSIFICATION_QUERY_KEYS.detail,
      companyId,
      payClassificationId,
    ],
    queryFn: async () => {
      return await coreApi.getPayClassification(payClassificationId)
    },
  })
  return transformPayClassificationOnGet(response)
}

/**
 * Creates/adds a new pay classification
 * @param payClassification the pay classification to add/create
 * @returns the newly created pay classification
 */
export const addPayClassification = async (
  payClassification: PayClassification
): Promise<PayClassification | null> => {
  if (!hasPermissionsAtVenue(PAY_CLASSIFICATION_PERMISSIONS)) return null

  const newPayClassification = await coreApi.createPayClassification(
    payClassification as PayClassificationRequest
  )
  return transformPayClassificationOnGet(newPayClassification)
}

/**
 * Updates an existing pay classification
 * @param payClassification the pay classification to update
 * @returns the updated pay classification
 */
export const updatePayClassification = async (
  payClassification: PayClassification
): Promise<PayClassification | null> => {
  if (!hasPermissionsAtVenue(PAY_CLASSIFICATION_PERMISSIONS)) return null

  const updatedPayClassification = await coreApi.updatePayClassification(
    payClassification.id!,
    payClassification as PayClassificationRequest
  )
  return transformPayClassificationOnGet(updatedPayClassification)
}

/**
 * Append a new time-bounded record to a Pay Classification's rate timeline.
 * @param payClassificationId the parent Pay Classification id
 * @param request the new record's validFrom, baseHourlyRate, and optional variations
 */
export const createTimelineRecord = async (
  payClassificationId: string,
  request: IPayClassificationTimelineRecordRequest
): Promise<void> => {
  await coreApi.createPayClassificationTimelineRecord(
    payClassificationId,
    request as PayClassificationTimelineRecordRequest
  )
}

/**
 * Update an existing timeline record. Rate, validFrom, and variations can all change.
 * @param payClassificationId the parent Pay Classification id
 * @param recordId the timeline record id to update
 * @param request the new values for the record
 */
export const updateTimelineRecord = async (
  payClassificationId: string,
  recordId: string,
  request: IPayClassificationTimelineRecordRequest
): Promise<void> => {
  await coreApi.updatePayClassificationTimelineRecord(
    payClassificationId,
    recordId,
    request as PayClassificationTimelineRecordRequest
  )
}

/**
 * Soft-delete a timeline record. Surrounding records absorb the gap server-side.
 * @param payClassificationId the parent Pay Classification id
 * @param recordId the timeline record id to delete
 */
export const deleteTimelineRecord = async (
  payClassificationId: string,
  recordId: string
): Promise<void> => {
  await coreApi.deletePayClassificationTimelineRecord(
    payClassificationId,
    recordId
  )
}
